import * as THREE from 'three';
import { animateTransform } from '../scene-transforms.js';
import { mergeStaticPropMeshes, weatheredMaterial } from './prop-detail.js';
import { segmentGeometry } from './natural-details.js';

/** Open clinker hull, rigging and working tackle, shared with the offshore fleet. */
export function createFishingBoat() {
  const group = animateTransform(new THREE.Group());
  const wood = weatheredMaterial(0x735143, 'wood'), dark = weatheredMaterial(0x42352e, 'wood'), rope = new THREE.MeshStandardMaterial({ color: 0xb8a47b, roughness: 1 });
  const iron = new THREE.MeshStandardMaterial({ color: 0x35444a, metalness: .5, roughness: .65 });
  const add = (g: THREE.BufferGeometry, m: THREE.Material, x=0,y=0,z=0) => { const o=new THREE.Mesh(g,m);o.position.set(x,y,z);o.castShadow=o.receiveShadow=true;group.add(o);return o; };
  const box = (w:number,h:number,d:number,x:number,y:number,z:number,m=wood) => add(new THREE.BoxGeometry(w,h,d),m,x,y,z);
  const beam = (a:number[],b:number[],r:number,m=rope) => add(segmentGeometry(a,b,r),m);
  const stations = [-3.4,-2.8,-1.9,-.8,.5,1.7,2.7,3.5], widths=[.35,.78,1.12,1.25,1.22,1.0,.65,.04];
  const width = (i:number,y:number) => widths[i]*(.48+y*.57);
  // Each narrow strake joins neighbouring hull stations. The open top exposes seats and ribs.
  for(const side of [-1,1]) for(let k=0;k<6;k++) for(let i=0;i<stations.length-1;i++) {
    const y=.02+k*.17, vertices:number[]=[];
    for(const [station,level] of [[i,y],[i+1,y],[i+1,y+.19],[i,y],[i+1,y+.19],[i,y+.19]]) vertices.push(side*width(station,level),level,stations[station]);
    const geo=new THREE.BufferGeometry();geo.setAttribute('position',new THREE.Float32BufferAttribute(vertices,3));geo.computeVertexNormals();
    add(geo,k%2?wood:dark).material.side=THREE.DoubleSide;
  }
  beam([0,-.12,-3.4],[0,-.12,3.5],.1,dark);
  for(const side of [-1,1]) for(let i=0;i<stations.length-1;i++) beam([side*width(i,1.03),1.03,stations[i]],[side*width(i+1,1.03),1.03,stations[i+1]],.075,dark);
  box(.75,.85,.07,0,.5,-3.4,dark);
  for(let i=1;i<7;i++) {
    for(const side of [-1,1]) {
      // Ribs follow the floor, bilge and inner hull, instead of cutting diagonally through the cockpit.
      const profile=[[0,.13],[side*width(i,.16)*.9,.16],[side*width(i,.43)*.93,.43],[side*width(i,.97)*.93,.97]];
      for(let j=1;j<profile.length;j++) beam([profile[j-1][0],profile[j-1][1],stations[i]],[profile[j][0],profile[j][1],stations[i]],.045,dark);
    }
    if(i%2===0) box(width(i,.8)*1.9,.1,.38,0,.82,stations[i]);
  }
  for(let i=0;i<5;i++) box(.21,.07,4.9,(i-2)*.23,.12,-.45);
  beam([0,.15,-.7],[0,6.9,-.7],.07,dark);
  // The boom and triangular sail run fore-and-aft, along the long axis of the hull.
  beam([0,2.315,-.55],[0,2.315,-2.9],.045,dark);
  for(const a of [[-1.05,1,-1.9],[1.05,1,-1.9],[0,1,3.2]]) beam(a,[0,6.6,-.7],.017);
  // A broad triangular stitched sail, gently bellying between the mast and boom.
  const vertices:number[]=[], n=12;
  const point=(u:number,v:number)=>[Math.sin(u*Math.PI)*Math.sin(v*Math.PI)*.34,2.36+v*3.65,-.7-u*2.05];
  for(let row=0;row<n;row++) for(let col=0;col<n-row;col++) {
    const a=point(col/n,row/n),b=point((col+1)/n,row/n),c=point(col/n,(row+1)/n);vertices.push(...a,...b,...c);
    if(col<n-row-1) vertices.push(...b,...point((col+1)/n,(row+1)/n),...c);
  }
  const fabric=new THREE.BufferGeometry();fabric.setAttribute('position',new THREE.Float32BufferAttribute(vertices,3));fabric.computeVertexNormals();
  add(fabric,weatheredMaterial(0xd9c5a5,'cloth',{side:THREE.DoubleSide}));
  // Edge ropes and seam stitches trace the same billowed surface as the sail.
  const seam=(at:(t:number)=>number[],radius:number)=>{
    for(let i=0;i<12;i++) beam(at(i/12),at((i+1)/12),radius);
  };
  seam(t=>point(t,0),.014); seam(t=>point(t,1-t),.014);
  for(let i=1;i<5;i++){ const v=i*.6/3.65; seam(t=>point(t*(1-v),v),.006); }
  // Stow the whole oar inside the starboard rail, resting on the benches and clear of the mast and net.
  const oarStart=new THREE.Vector3(.36,.905,1.85),oarTip=new THREE.Vector3(.36,.905,-2.35),oarDirection=oarTip.clone().sub(oarStart).normalize();
  beam(oarStart.toArray(),oarTip.toArray(),.033,dark);
  const paddleCenter=oarTip.clone().addScaledVector(oarDirection,.375);
  const paddle=box(.18,.045,.75,paddleCenter.x,paddleCenter.y,paddleCenter.z);
  paddle.quaternion.setFromUnitVectors(new THREE.Vector3(0,0,1),oarDirection);
  // Transom-hung rudder: blade below the waterline, two working hinges and a tiller reaching into the stern.
  beam([0,-.65,-3.55],[0,1.25,-3.55],.06,dark);
  const rudderOutline=new THREE.Shape();rudderOutline.moveTo(0,.65);rudderOutline.lineTo(.2,.27);rudderOutline.lineTo(.58,.11);rudderOutline.lineTo(.7,-.67);rudderOutline.lineTo(.35,-.83);rudderOutline.lineTo(0,-.65);rudderOutline.closePath();
  const rudder=add(new THREE.ExtrudeGeometry(rudderOutline,{depth:.12,bevelEnabled:true,bevelSize:.012,bevelThickness:.01,bevelSegments:1}),wood,-.06,0,-3.55);rudder.rotation.y=Math.PI/2;
  for(const y of [.35,.8]) {
    beam([0,y,-3.425],[0,y,-3.55],.035,iron);
    add(new THREE.TorusGeometry(.067,.014,5,12),iron,0,y,-3.55).rotation.x=Math.PI/2;
  }
  beam([0,1.17,-3.55],[.08,1.17,-2.48],.045,dark);
  for(let i=0;i<4;i++) add(new THREE.TorusGeometry(.22+i*.018,.022,5,18),rope,-.65,.87+i*.025,.5).rotation.x=Math.PI/2;
  box(.5,.4,.55,.45,.36,-2.3,dark);
  for(let i=0;i<6;i++) {box(.51,.025,.025,.45,.19+i*.06,-2.02,rope);box(.025,.36,.57,.23+i*.088,.36,-2.3,rope);}
  // A folded warp-and-weft net rests on the forward bench, rather than hanging over an empty bay.
  const net=(u:number,v:number)=>[(u-.5)*.95,.9+Math.sin(u*Math.PI)*Math.sin(v*Math.PI)*.04,2.7+(v-.5)*.42];
  for(let i=0;i<7;i++)for(let j=0;j<6;j++){
    beam(net(i/6,j/6),net(i/6,(j+1)/6),.011,rope);
    beam(net(j/6,i/6),net((j+1)/6,i/6),.011,rope);
  }
  for(let i=0;i<4;i++)add(new THREE.SphereGeometry(.045,7,5),wood,-.38+i*.25,.93,2.46);
  const light=animateTransform(add(new THREE.CylinderGeometry(.12,.12,.27,8),new THREE.MeshBasicMaterial({color:0xffbf72}),0,6.94,-.7));
  box(.32,.045,.3,0,6.77,-.7,iron);add(new THREE.ConeGeometry(.23,.18,4),iron,0,7.15,-.7).rotation.y=Math.PI/4;
  for(const x of [-.13,.13]) for(const z of [-.83,-.57]) beam([x,6.77,z],[x,7.07,z],.012,iron);
  mergeStaticPropMeshes(group);
  return {group,light};
}
